import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { signInWithEmailAndPassword, AuthError } from "firebase/auth"
import { auth } from "../lib/firebase"
import { Loader2 } from "lucide-react"

const Login: React.FC = () => {
	const navigate = useNavigate()
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [error, setError] = useState("")
	const [isLoading, setIsLoading] = useState(false)

	const getErrorMessage = (error: AuthError): string => {
		switch (error.code) {
			case "auth/invalid-email":
				return "Please enter a valid email address."
			case "auth/user-disabled":
				return "This account has been disabled. Please contact support."
			case "auth/user-not-found":
				return "No account found with this email address."
			case "auth/wrong-password":
				return "Incorrect password. Please try again."
			case "auth/invalid-credential":
				return "Invalid email or password. Please check your credentials and try again."
			case "auth/too-many-requests":
				return "Too many failed login attempts. Please try again later."
			case "auth/network-request-failed":
				return "Network error. Please check your connection and try again."
			case "auth/operation-not-allowed":
				return "Email/password sign-in is not enabled. Please contact support."
			default:
				return "An error occurred during sign-in. Please try again."
		}
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError("")
		setIsLoading(true)

		try {
			await signInWithEmailAndPassword(auth, email, password)
			navigate("/")
		} catch (err) {
			const authError = err as AuthError
			setError(getErrorMessage(authError))
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
			<div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg">
				<div className="flex flex-col items-center">
					<h2 className="text-3xl font-bold text-center text-gray-900">
						Welcome Back
					</h2>
					<p className="mt-2 text-center text-gray-600">
						Expert Neurological Case Management
					</p>
				</div>

				<form onSubmit={handleSubmit} className="mt-8 space-y-6">
					<div className="space-y-4">
						<div>
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-700"
							>
								Email Address
							</label>
							<input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								className="w-full px-4 py-3 mt-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none transition"
								placeholder="you@example.com"
							/>
						</div>
						<div>
							<label
								htmlFor="password"
								className="block text-sm font-medium text-gray-700"
							>
								Password
							</label>
							<input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								className="w-full px-4 py-3 mt-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none transition"
							/>
						</div>
					</div>

					{error && (
						<div className="p-3 text-sm text-red-600 bg-red-50 rounded-md border border-red-200">
							{error}
						</div>
					)}

					<button
						type="submit"
						disabled={isLoading}
						className="w-full px-4 py-3 font-medium text-white bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition disabled:opacity-70 disabled:cursor-not-allowed"
					>
						{isLoading ? (
							<span className="flex items-center justify-center">
								<Loader2 className="w-5 h-5 mr-2 animate-spin" />
								Signing in...
							</span>
						) : (
							"Sign in"
						)}
					</button>
				</form>
			</div>
		</div>
	)
}

export default Login
